import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { USER_CHANGE_PASS_URL } from '../../../../api/apiRoutes';
import { readStoredUserToken } from "../../../../store/auth/authMiddleware";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import { setSuccessMessage } from "../../../../store/alerts/alertsActions";
import { passValidator } from "../../../../../utils/formsValidation";
import { useTranslation } from "react-i18next";
import EAButton from "../../../shared/Buttons/EAButton";
import EAModal from "../../../shared/Modal/EAModal";
import EATextField from "../../../shared/TextField/EATextField";
import EAAlert from "../../../shared/EAAlert";
import { MdOutlineLock } from "react-icons/md";
import inputsValidation from "../../../../../messages/inputValidation";
import { errorMessageFromApi } from "../../../../utils/apiErrorMessages";
import { useDispatch } from "react-redux";

type ChangePasswordInputs = {
  oldPassword: string;
  newPassword: string;
};

const ChangePasswordModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { post } = useApiRequest();
  const facilityIds = useFacility();

  const [openModal, setOpenModal] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordInputs>({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
    },
  });

  const handleOpenModal = () => {
    setOpenModal(true);
    setFormError("");
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormError("");
    reset();
  };

  const handleChangePassword: SubmitHandler<ChangePasswordInputs> = async (data) => {
    setFormError("");
    setIsSubmitting(true);

    const parsedUserToken = readStoredUserToken()?.sessionToken || "";

    const body = {
      oldpassword: data.oldPassword,
      password: data.newPassword,
      token: parsedUserToken,
    };

    const { isError, errorTable } = await post(USER_CHANGE_PASS_URL(facilityIds), body);

    if (!isError) {
      dispatch(setSuccessMessage(t("operacja_przebiegla_pomyslnie")));
      handleCloseModal();
    } else {
      const errorMessage = errorMessageFromApi({ isError, errorTable }) || t("wystapil_blad");
      setFormError(errorMessage);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <div className="settings-section">
        <div className="settings-section__header">
          <h5>{t("zmiana_hasla")}</h5>
        </div>

        <div className="settings-section__content">
          <div className="settings-section__actions">
            <EAButton
              title={t("zmien_haslo")}
              onClick={handleOpenModal}
              variant="outlined"
              startIcon={<MdOutlineLock />}
            />
          </div>
        </div>
      </div>

      {openModal && (
        <EAModal
          isOpen={openModal}
          onClose={handleCloseModal}
          title={t("zmiana_hasla")}
          acceptButtonLabel={t("zapisz")}
          onAccept={handleSubmit(handleChangePassword)}
          loading={isSubmitting}
        >
          <form onSubmit={handleSubmit(handleChangePassword)} className="change-password-form">
            <EATextField
              label={t("stare_haslo")}
              type="password"
              name="oldPassword"
              fullWidth
              error={!!errors.oldPassword}
              helperText={inputsValidation(errors, "oldPassword")}
              register={register("oldPassword", { required: true })}
            />
            <EATextField
              label={t("nowe_haslo")}
              type="password"
              name="newPassword"
              fullWidth
              error={!!errors.newPassword}
              register={register("newPassword", { ...passValidator(true) })}
              helperText={inputsValidation(errors, "newPassword")}
            />

            {formError && <EAAlert message={formError} type="error" compact />}
          </form>
        </EAModal>
      )}
    </>
  );
};

export default ChangePasswordModal;
